import { useCallback, useEffect, useState } from 'react';

/**
 * Shared 12h/24h clock preference. Lives in `common/` so any module can read it
 * without importing another feature module (the calendar already imports tasks,
 * so tasks can't import the calendar back). The calendar's `useCalendar24Hour`
 * re-exports this hook, and both read the SAME localStorage key + change event,
 * so every surface (calendar grid, calendar settings toggle, task time picker)
 * stays in lockstep.
 */
const STORAGE_KEY = 'xavior:calendar:use24h';
const CHANGE_EVENT = 'xavior:calendar-24h-changed';

function read24h(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function useClock24Hour(): [boolean, (v: boolean) => void] {
  const [use24h, setState] = useState(read24h);

  useEffect(() => {
    const handler = () => setState(read24h());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setUse24h = useCallback((v: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(v));
    } catch {
      /* best-effort — in-memory state still updates below */
    }
    setState(v);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [use24h, setUse24h];
}
